import { Button, Divider, message, Popover, Typography } from 'antd';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatToINR } from '../../../Utils/utils';

import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { sum } from 'es-toolkit';
import { Sheet } from 'react-modal-sheet';
import { AppContext, RefreshStatus } from '../../../App';
import { ReactComponent as LinkIcon } from '../../../Assets/linkIcon.svg';
import {
  refreshApi,
  revokeConsentAPI,
} from '../../../Services/PFMClient/DashboardService';
import { FlowType, prefix } from '../../../Utils/constants';
import { EVENTCODES, pushEventToParent } from '../../../Utils/eventUtils';
import { getImagePath } from '../../../Utils/assetUtils';
import HeaderSubHeader from '../Header/HeaderSubHeader';
import NavigationHeader from '../Header/NavigationHeader';
import styles from './index.module.css';

const Stocks: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    stocksData,
    setStocksData,
    startAdditionalAccountJourney,
    statusData,
    setStatusPolling,
    setIsAccountDetailsFetched,
    setAnalyticsStatusStocks,
    setAnalyticsStatus,
    dataRefreshStatus,
    setBackButtonHandler,
    isAssetDashboardRendered,
  }: any = useContext(AppContext);

  // Callback function for back navigation
  const handleBackNavigation = useCallback(() => {
    // Check if we came from AssetsDashboard
    if (isAccountDetailsOpen) {
      setIsAccountDetailsOpen(false);
    } else if (revokeConsentSheet) {
      setRevokeConsentSheet(false);
    } else if (isAssetDashboardRendered.current) {
      navigate('/home');
    } else {
      pushEventToParent({ eventCode: 'BACK_BUTTON_PRESSED' });
    }
  }, [location.state, navigate]);

  const refreshData = () => {
    if (
      statusData.current.accountDetails.filter((accountDetail: any) => {
        return accountDetail.status === 'REVOKE_INITIATED';
      }).length !== 0
    ) {
      message.info(
        <Typography.Text style={{ fontSize: 10, fontWeight: 600 }}>
          Consent revocation in progress. Please try again in some time.
        </Typography.Text>
      );
    } else {
      pushEventToParent({
        eventCode: EVENTCODES.USER_INITIATED_REFRESH_BUTTON_CLICKED,
      });
      refreshApi()
        .then(() => {
          setAnalyticsStatus('PENDING');
          setIsAccountDetailsFetched(false);
          setStatusPolling((prev: any) => prev + 1);
          pushEventToParent({
            eventCode: EVENTCODES.REFRESH_PROCESSING,
          });
        })
        .catch(() => {
          pushEventToParent({
            eventCode: EVENTCODES.REFRESH_FAILED,
          });
          message.error('Something went wrong. Try again after sometime');
        });
    }
  };

  const refreshTime = useMemo(() => {
    return statusData.current.nextRefreshTime;
  }, [statusData.current.nextRefreshTime]);

  const getStockData = useMemo(() => {
    const currentValues = stocksData?.map((stock: any) =>
      Number(
        stock?.rawData?.Summary?.currentValue
          ? stock?.rawData?.Summary?.currentValue
          : 0
      )
    );
    return Number(sum(currentValues).toString());
  }, []);

  const stocksAccounts = useMemo(() => {
    return statusData.current.accountDetails.filter((acc: any) => {
      return (
        (acc.fiType === 'EQUITIES' || acc.fiType === 'ETF') &&
        acc.status === 'ACTIVE'
      );
    });
  }, [statusData.current.accountDetails]);

  const getStocksSubtitle = useMemo(() => {
    const accountDetails = stocksAccounts.filter(
      (acc: any) => acc.lastRequestFIStatus === 'DELIVERED'
    );
    if (accountDetails.length > 0) {
      return (
        <>
          <LinkIcon /> {accountDetails.length}
        </>
      );
    }
    return '';
  }, []);

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.onscroll = function () {
      if (window.scrollY > 18) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.scrollTo(0, 0);
  }, []);

  const handleImageError = (event: any) => {
    event.target.src = getImagePath(prefix ? `${prefix}/images/bank.svg` : 'images/bank.svg');
  };

  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const revokeConsent = () => {
    setIsButtonLoading(true);
    Promise.all(
      stocksAccounts
        .map((r: any) => r.referenceId)
        .map((ref: string) =>
          revokeConsentAPI({
            referenceId: ref,
          })
        )
    ).then(() => {
      setStocksData([]);
      setRevokeConsentSheet(false);
      setIsAccountDetailsOpen(false);
      setAnalyticsStatusStocks('PENDING');
      setStatusPolling((prev: any) => prev + 1);
      setIsButtonLoading(false);
      pushEventToParent({ eventCode: 'BACK_BUTTON_PRESSED' });
    });
  };

  const [revokeConsentSheet, setRevokeConsentSheet] = useState(false);
  const [isAccountDetailsOpen, setIsAccountDetailsOpen] = useState(false);

  useEffect(() => {
    window.onpopstate = () => {
      // Push the user forward again to prevent back
      window.history.pushState(null, '', window.location.href);
    };
    setBackButtonHandler(() => {
      return () => {
        // Check if we came from AssetsDashboard
        if (isAccountDetailsOpen) {
          setIsAccountDetailsOpen(false);
        } else if (revokeConsentSheet) {
          setRevokeConsentSheet(false);
        } else if (isAssetDashboardRendered.current) {
          navigate('/home');
        } else {
          pushEventToParent({ eventCode: 'BACK_BUTTON_PRESSED' });
        }
      };
    });
  }, [isAccountDetailsOpen, revokeConsentSheet]);

  return (
    <div className={styles.spends_home_container}>
      <Sheet
        className='accountDetailsSheet'
        isOpen={isAccountDetailsOpen}
        detent='content-height'
        onClose={() => {
          setIsAccountDetailsOpen(false);
        }}
      >
        <Sheet.Backdrop
          onTap={() => setIsAccountDetailsOpen(false)}
        ></Sheet.Backdrop>
        <Sheet.Container>
          <div className={styles.accountDetails}>
            <div className={styles.closeIconContainer}>
              <div
                className={styles.closeIcon}
                onClick={() => {
                  setIsAccountDetailsOpen(false);
                }}
              >
                <Typography.Text style={{ color: '#fff', fontWeight: 500 }}>
                  X
                </Typography.Text>
              </div>
            </div>

            <HeaderSubHeader
              header='Do you really want to revoke the consent?'
              subHeader='On revoking, you will not be able to track your demant accounts.'
            />

            <div className={styles.consentRevokeButtonContainer}>
              <Button
                className={styles.consentRevokeGoBack}
                onClick={() => {
                  pushEventToParent({
                    eventCode: EVENTCODES.GO_BACK_CONSENT_REVOKE,
                  });
                  setRevokeConsentSheet(true);
                  setIsAccountDetailsOpen(false);
                }}
              >
                <Typography.Text
                  style={{
                    fontWeight: 600,
                    color: 'var(--primary-color)',
                  }}
                >
                  Go Back
                </Typography.Text>
              </Button>
              <Button
                loading={isButtonLoading}
                className={styles.consentRevokeConfirm}
                onClick={() => {
                  if (dataRefreshStatus === RefreshStatus.IN_PROGRESS) {
                    message.info(
                      <Typography.Text
                        style={{ fontSize: 10, fontWeight: 600 }}
                      >
                        Your account data refresh is in progress. Please try
                        again in some time.
                      </Typography.Text>
                    );
                  } else {
                    pushEventToParent({
                      eventCode: EVENTCODES.CONSENT_REVOKE_CLICKED,
                    });
                    revokeConsent();
                  }
                }}
              >
                <Typography.Text style={{ color: '#fff', fontWeight: 600 }}>
                  Revoke
                </Typography.Text>
              </Button>
            </div>
          </div>
        </Sheet.Container>
      </Sheet>

      <Sheet
        isOpen={revokeConsentSheet}
        detent='content-height'
        className='pbRevokeConsentSheet'
        onClose={() => {
          setRevokeConsentSheet(false);
        }}
      >
        <Sheet.Backdrop
          onTap={() => setRevokeConsentSheet(false)}
        ></Sheet.Backdrop>

        <Sheet.Container>
          <div className={styles.consentRevokeSheetContainer}>
            <div className={styles.closeIconContainer}>
              <div
                className={styles.closeIcon}
                onClick={() => {
                  setRevokeConsentSheet(false);
                }}
              >
                <Typography.Text style={{ color: '#fff', fontWeight: 500 }}>
                  X
                </Typography.Text>
              </div>
            </div>

            <HeaderSubHeader
              header='Account Details'
              subHeader='Accounts linked with Paisabazaar'
            />

            <div
              style={{
                margin: '8px 0px 16px 0px',
                overflow: 'auto',
                height: '50%',
                display: 'flex',
                flexDirection: 'column',
                paddingBottom: 1,
              }}
            >
              {stocksData.map((stocks: any) => (
                <div className={styles.accountDetaisListItemFirst}>
                  <Typography.Text className={styles.fipTitle}>
                    {stocks.rawData.brokerName ?? stocks.rawData.fipName}
                  </Typography.Text>

                  <Typography.Text className={styles.accountNumberText}>
                    XXXX {stocks.rawData.maskedDematId.slice(-4)}
                  </Typography.Text>
                </div>
              ))}

              {stocksAccounts
                .filter((acc: any) => acc.lastRequestFIStatus !== 'DELIVERED')
                .map((acc: any) => (
                  <div className={styles.accountDetailsFailure}>
                    <div
                      style={{
                        flex: '70%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography.Text className={styles.fipTitle}>
                        {acc.fipName}
                      </Typography.Text>

                      <Typography.Text className={styles.accountNumberText}>
                        XXXX {acc.maskedAccountNumber.slice(-4)}
                      </Typography.Text>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        flex: '30%',
                      }}
                    >
                      <Popover
                        content={
                          <Typography.Text
                            style={{ fontSize: 12, fontWeight: 600 }}
                          >
                            We were unable to fetch details from your repository
                          </Typography.Text>
                        }
                        trigger='click'
                        overlayClassName={styles.popOverStyle}
                      >
                        <InfoCircleOutlined
                          style={{
                            width: 24,
                            height: 24,
                            display: 'flex',
                            justifyContent: 'center',
                            color: 'rgb(27, 29, 199)',
                          }}
                        />
                      </Popover>

                      <Typography.Text className={styles.accountNumberText}>
                        Data Fetch failed
                      </Typography.Text>
                    </div>
                  </div>
                ))}
            </div>

            <div className={styles.consentRevokeButtonContainer}>
              <Button
                className={styles.consentRevokeGoBack}
                onClick={() => {
                  setRevokeConsentSheet(false);
                  setIsAccountDetailsOpen(true);
                  pushEventToParent({
                    eventCode: EVENTCODES.REVOKE_YOUR_CONSENT_CLICKED,
                  });
                }}
              >
                <Typography.Text
                  style={{
                    fontWeight: 600,
                    color: 'var(--primary-color)',
                  }}
                >
                  Revoke Your Consent
                </Typography.Text>
              </Button>
            </div>
          </div>
        </Sheet.Container>
      </Sheet>

      <NavigationHeader header='Stocks' onBack={handleBackNavigation} />

      <div
        className={`${styles.spendsHeaderContainer} ${styles.marginTop12Spends}`}
      >
        <div className={styles.spendsHeaderTextContainer}>
          <Typography.Text className={styles.commonHeaderSubtitle}>
            Your Total Stocks Value
          </Typography.Text>
          <div style={{ display: 'flex', gap: 16 }}>
            <Typography.Text className={styles.commonHeaderTitle}>
              {formatToINR(getStockData.toString())}
            </Typography.Text>
          </div>

          <Typography.Link
            onClick={() => setRevokeConsentSheet(true)}
            className={styles.commonHeaderLinkAccountTitle}
          >
            {getStocksSubtitle} linked accounts
          </Typography.Link>
        </div>

        <Button
          className={styles.addBankButton}
          onClick={() => {
            pushEventToParent({ eventCode: EVENTCODES.ADD_ACCOUNT_STOCKS });
            startAdditionalAccountJourney(FlowType.STOCKS);
          }}
        >
          + Add Account
        </Button>
      </div>

      <div style={{ marginTop: 16, padding: '0px 16px 16px 16px' }}>
        {stocksData.map((stocks: any) =>
          stocks.rawData.Summary.Investment.Holdings.Holding.map(
            (holding: any) => {
              var ltp =
                stocks.rawData.type &&
                stocks.rawData.type.toLowerCase() === 'etf'
                  ? holding.nav
                  : holding.lastTradedPrice;
              return (
                <div
                  style={{
                    background: '#F2F7FF',
                    padding: 16,
                    marginBottom: 12,
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <div
                      style={{
                        border: '1px solid #E4E4E3',
                        height: 40,
                        width: 40,
                        borderRadius: 8,
                        background: '#FDFDFD',
                      }}
                    >
                      <img
                        src={`https://logo.ignosis.ai`}
                        style={{
                          objectFit: 'contain',
                          padding: 7,
                          height: 26,
                          width: 26,
                        }}
                        onError={handleImageError}
                      />
                    </div>
                    <Typography.Text
                      style={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {holding.issuerName ?? holding.isinDescription}
                    </Typography.Text>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: 12,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ maxWidth: '23%', minWidth: '23%' }}>
                      <Typography.Text
                        style={{
                          fontSize: 10,
                          fontWeight: 500,
                          color: '#6A6D71',
                        }}
                      >
                        LTP
                      </Typography.Text>
                      <Typography.Text style={{ fontWeight: 600 }}>
                        {!Number.isNaN(parseInt(ltp))
                          ? formatToINR(parseInt(ltp).toString())
                          : formatToINR('0')}
                      </Typography.Text>
                    </div>
                    <Divider
                      style={{
                        backgroundColor: '#E4E4E3',
                        height: 20,
                        marginInline: '8px 20px',
                      }}
                      type='vertical'
                    />
                    <div style={{ maxWidth: '23%', minWidth: '23%' }}>
                      <Typography.Text
                        style={{
                          fontSize: 10,
                          fontWeight: 500,
                          color: '#6A6D71',
                        }}
                      >
                        Quantity
                      </Typography.Text>
                      <Typography.Text style={{ fontWeight: 600 }}>
                        {holding.units}
                      </Typography.Text>
                    </div>
                    <Divider
                      style={{
                        backgroundColor: '#E4E4E3',
                        height: 20,
                        marginInline: '8px 20px',
                      }}
                      type='vertical'
                    />
                    <div style={{ maxWidth: '35%', minWidth: '35%' }}>
                      <Typography.Text
                        style={{
                          fontSize: 10,
                          fontWeight: 500,
                          color: '#6A6D71',
                        }}
                      >
                        Current Value
                      </Typography.Text>
                      <Typography.Text style={{ fontWeight: 600 }}>
                        {formatToINR(
                          (
                            (!Number.isNaN(parseInt(ltp)) ? parseInt(ltp) : 0) *
                            holding.units
                          ).toString()
                        )}
                      </Typography.Text>
                    </div>
                  </div>
                </div>
              );
            }
          )
        )}
      </div>
    </div>
  );
};

export default Stocks;
